/**
 * Configuration system types and metadata.
 * Defines the structure for game constants, player settings, and developer settings.
 */

/**
 * Visibility level for a configuration setting.
 */
export enum ConfigVisibility {
  /** Visible and editable by players in settings menu */
  PLAYER = 'player',
  /** Visible and editable by developers in dev admin panel (dev environment only) */
  DEVELOPER = 'developer',
  /** Not visible to players, but editable by developers (dev environment only) */
  DEVELOPER_HIDDEN = 'developer_hidden',
  /** Not visible or editable by anyone (internal constants) */
  INTERNAL = 'internal',
}

/**
 * Category for organizing settings in UI.
 */
export enum ConfigCategory {
  CAMERA = 'camera',
  AUDIO = 'audio',
  GRAPHICS = 'graphics',
  CONTROLS = 'controls',
  GAMEPLAY = 'gameplay',
  DESIGN = 'design',
  BUILDINGS = 'buildings',
  UNITS = 'units',
  DEBUG = 'debug',
  PERFORMANCE = 'performance',
}

/**
 * Metadata for a configuration setting.
 */
export interface ConfigMetadata {
  /** Display name for UI */
  label: string;
  /** Description/tooltip text */
  description?: string;
  /** Category for grouping in UI */
  category: ConfigCategory;
  /** Visibility and editability level */
  visibility: ConfigVisibility;
  /** Minimum value (for numeric settings) */
  min?: number;
  /** Maximum value (for numeric settings) */
  max?: number;
  /** Step increment (for numeric settings) */
  step?: number;
  /** Options for dropdown/select (for enum settings) */
  options?: Array<{ value: string | number; label: string }>;
}

/**
 * Base configuration schema definition.
 * Maps setting keys to their metadata.
 */
export type ConfigSchema<T = Record<string, unknown>> = {
  [K in keyof T]: ConfigMetadata;
};

/**
 * Configuration value type.
 */
export type ConfigValue = string | number | boolean;

/**
 * Configuration object type.
 */
export type ConfigObject = Record<string, ConfigValue>;
